/**
 * The pairs a match rule could not decide alone.
 *
 * Both candidates are shown side by side with the differing fields highlighted,
 * and the signals that produced the score are spelled out — nobody should be
 * asked to merge two records on the strength of a number.
 *
 * Four things can be done with a pair, and only two of them are a verdict.
 * **Merge** and **Different things** answer the question. **Edit** corrects a
 * value on either side, and **Resubmit** asks the same rules again afterwards —
 * because a pair that only looked ambiguous because of a typo should be decided
 * by fixing the typo, not by a steward guessing on its behalf.
 */

import { useState } from 'react'
import { EmptyState, Modal, SkeletonRows } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Value } from '../components'
import { useResolveReview, useResubmitReview, useReviews, useUpdateRecord } from '../hooks'
import type { GraphModel, RecordRow } from '../types/brain.types'

export function ReviewQueue({
  graphId,
  model,
  nodeTypeKey,
}: {
  graphId: string
  model: GraphModel
  /** Empty means every entity — the picker above this view owns the choice. */
  nodeTypeKey?: string
}) {
  // The queue is filtered by node type id, but the picker speaks in keys.
  const nodeTypeId = nodeTypeKey
    ? model.node_types.find((n) => n.key === nodeTypeKey)?.node_type_id
    : undefined
  const reviews = useReviews(graphId, 'PENDING', nodeTypeId)
  const resolve = useResolveReview(graphId)
  const resubmit = useResubmitReview(graphId)
  const toast = useToast()
  const [editing, setEditing] = useState<RecordRow | null>(null)

  if (reviews.isLoading) return <SkeletonRows rows={3} columns={4} label="Loading the review queue…" />

  if (!reviews.data?.length) {
    return (
      <EmptyState title="Nothing needs a decision">
        Pairs land here when they score between a match rule's two thresholds — similar enough to
        be suspicious, not similar enough to merge on their own.
      </EmptyState>
    )
  }

  return (
    <div className="stack">
      {reviews.data.map((review) => {
        const a = review.candidate_a
        const b = review.candidate_b
        const nodeType = model.node_types.find((n) => n.node_type_id === review.node_type_id)
        const keys = nodeType?.properties.map((p) => p.key) ?? unionKeys(a, b)

        return (
          <div key={review.review_id} className="rulecard">
            <div className="rulehead">
              <span className="rulename">
                Are these the same {nodeType?.key ?? 'record'}?
              </span>
              <span className="badge badge-warn mono">{review.score.toFixed(3)}</span>
              <span className="spacer" />
              {/* Not a verdict: ask the same rules again, now that the records
                  or the rules have changed. */}
              <button
                className="btn btn-sm"
                disabled={resubmit.isPending}
                title="Score this pair again against the records as they are now."
                onClick={async () => {
                  try {
                    const outcome = await resubmit.mutateAsync(review.review_id)
                    if (outcome.outcome === 'merged') {
                      toast.success(
                        'Merged',
                        `Rescored to ${outcome.score.toFixed(3)}, which clears the merge bar.`,
                      )
                    } else if (outcome.outcome === 'distinct') {
                      toast.success(
                        'No longer a match',
                        'Rescored below the review bar, so the pair has left the queue.',
                      )
                    } else {
                      toast.success(
                        'Still waiting',
                        `Rescored to ${outcome.score.toFixed(3)} — still between the two bars.`,
                      )
                    }
                  } catch (error) {
                    toast.error(error)
                  }
                }}
              >
                {resubmit.isPending ? 'Rescoring…' : 'Resubmit'}
              </button>
              <button
                className="btn btn-sm"
                disabled={resolve.isPending}
                onClick={async () => {
                  try {
                    await resolve.mutateAsync({ reviewId: review.review_id, action: 'reject' })
                    toast.success('Kept them separate. This pair will not come back.')
                  } catch (error) {
                    toast.error(error)
                  }
                }}
              >
                Different things
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={resolve.isPending}
                onClick={async () => {
                  if (
                    !window.confirm(
                      'Merge these two records? The survivorship rules decide each field, and every decision is written to the audit.',
                    )
                  )
                    return
                  try {
                    await resolve.mutateAsync({ reviewId: review.review_id, action: 'merge' })
                    toast.success('Merged.')
                  } catch (error) {
                    toast.error(error)
                  }
                }}
              >
                Merge them
              </button>
            </div>

            <div className="diffpair">
              <CandidateCard record={a} keys={keys} other={b} onEdit={() => setEditing(a)} />
              <CandidateCard record={b} keys={keys} other={a} onEdit={() => setEditing(b)} />
            </div>

            <div className="stack" style={{ gap: 3 }}>
              <span className="small faint">Why they scored that</span>
              {Object.entries(review.signals as Record<string, Record<string, unknown>>).map(
                ([key, signal]) => (
                  <div className="row-tight small" key={key}>
                    <span className="mono" style={{ minWidth: 150 }}>
                      {key}
                    </span>
                    <span className="faint">{String(signal.method ?? '')}</span>
                    <span className="muted">
                      {signal.reason
                        ? String(signal.reason)
                        : typeof signal.value === 'number'
                          ? signal.value.toFixed(3)
                          : String(signal.value)}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
        )
      })}

      {editing && (
        <EditRecordDialog graphId={graphId} record={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  )
}

function unionKeys(a: RecordRow | null, b: RecordRow | null): string[] {
  return Array.from(new Set([...Object.keys(a?.properties ?? {}), ...Object.keys(b?.properties ?? {})]))
}

function EditRecordDialog({
  graphId,
  record,
  onClose,
}: {
  graphId: string
  record: RecordRow
  onClose: () => void
}) {
  const update = useUpdateRecord(graphId)
  const toast = useToast()
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(record.properties ?? {}).map(([k, v]) => [k, v == null ? '' : String(v)]),
    ),
  )

  return (
    <Modal
      title={`Edit ${record.label ?? 'this record'}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={update.isPending}
            onClick={async () => {
              try {
                await update.mutateAsync({ entityId: record.entity_id, properties: values })
                toast.success(
                  'Values saved',
                  'The pair keeps its old score until you resubmit it.',
                )
                onClose()
              } catch (error) {
                toast.error(error)
              }
            }}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>
          Correcting a value here does not re-decide the pair. Resubmit it afterwards and the
          rules will score it again.
        </p>
        {Object.entries(values).map(([key, value]) => (
          <label key={key} className="stack" style={{ gap: 4 }}>
            <span className="small mono">{key}</span>
            <input
              className="input"
              value={value}
              onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
            />
          </label>
        ))}
      </div>
    </Modal>
  )
}

function CandidateCard({
  record,
  keys,
  other,
  onEdit,
}: {
  record: RecordRow | null
  keys: string[]
  other: RecordRow | null
  onEdit: () => void
}) {
  if (!record) {
    return (
      <div className="card">
        <p className="small faint">This record no longer exists.</p>
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ minWidth: 0 }}>
          <strong className="truncate">{record.label ?? record.entity_id.slice(0, 8)}</strong>
          <div className="small faint mono">{record.entity_id.slice(0, 8)}</div>
        </span>
        {/* Correcting this side is how an ambiguous pair usually gets decided:
            the values were the problem, not the rules. */}
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={onEdit}>
          Edit
        </button>
      </div>
      <div className="kvgrid">
        {keys.map((key) => {
          const mine = record.properties[key]
          const theirs = other?.properties[key]
          const differs =
            mine !== undefined &&
            theirs !== undefined &&
            JSON.stringify(mine) !== JSON.stringify(theirs)
          return (
            <div key={key} style={{ display: 'contents' }}>
              <div>{key}</div>
              <div className="diffcell" data-differs={differs}>
                <Value value={mine} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
